import { ATTRACT_SEED } from '../content/attract';
import { BOMBS, DEFAULT_BOMB_ID, resolveBomb } from '../content/bombs';
import { CAMPAIGN_START, levelAt, WORLDS } from '../content/campaign';
import type { DefIndex } from '../sim/defs';
import { DEFAULT_SHIP_ID, DEFAULT_WEAPON_ID } from '../sim/defs';
import { Difficulty } from '../sim/difficulty';
import { DEFAULT_SECONDARY_ID } from '../sim/secondary';
import { syncStats } from '../sim/systems/progressionSystem';
import type { World } from '../sim/World';
import { loadoutModifiers, resolveSecondary, resolveShip, resolveWeapon } from './loadout';

/* ═══════════════════════════════════════════════════════════════════════════
   L'attract mode — la MISE EN PLACE du monde de démonstration.

   Séparée des ENTRÉES (`content/attract.ts`) parce qu'elle vit à un autre
   étage : monter le monde demande le loadout, la campagne et les bombes, tout
   ce que `content/` ne connaît pas. Les entrées, elles, ne dépendent que des
   tables trigonométriques et restent donc dans `content/`, sous la garde
   ESLint qui interdit `Math.sin`.

   La règle cardinale : cette mise en place ne LIT JAMAIS la sauvegarde. Le
   loadout est celui de SÉRIE, il n'y a ni bonus méta ni contrainte, et la
   graine est fixe. C'est la condition pour que la boucle soit reproductible au
   bit près — chez ce joueur comme chez tout autre — et donc un vrai test de
   déterminisme.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Difficulté de la démo : la plus clémente. Le vaisseau scripté doit survivre
 *  les 30 s sans intervention, et le test de déterminisme le vérifie. */
const ATTRACT_DIFFICULTY = Difficulty.Recrue;

/** Coque de la démo. Exportée pour que la vue cale la bonne silhouette. */
export const ATTRACT_SHIP_ID = DEFAULT_SHIP_ID;

/**
 * Prépare `world` pour une boucle de démonstration, de façon 100 %
 * déterministe et indépendante de la sauvegarde.
 *
 * Miroir volontaire du cœur de `startRun`, mais aux entrées FIGÉES : mêmes
 * étapes, même ordre (reset → loadout → syncStats → armPlayer → startLevel) —
 * l'ordre est verrouillé par les mêmes raisons (les vies dépendent des mods
 * déjà appliqués). Appelée à la fois par `main.ts` (sur le monde réel) et par
 * le test de déterminisme (sur deux mondes neufs, pour prouver l'égalité).
 */
export function armAttract(world: World, defs: DefIndex): void {
  world.reset(ATTRACT_SEED);
  const ship = defs.ships[resolveShip(DEFAULT_SHIP_ID, defs.ships, defs.ships)]!;
  const weapon = defs.weapons[resolveWeapon(DEFAULT_WEAPON_ID, defs.weapons, defs.weapons)]!;
  world.progress.baseMods.push(...loadoutModifiers(ship, weapon));
  syncStats(world);
  world.armPlayer(
    ATTRACT_DIFFICULTY,
    resolveSecondary(DEFAULT_SECONDARY_ID, defs.secondaries, defs.secondaries),
    { bomb: resolveBomb(DEFAULT_BOMB_ID, BOMBS), constraints: 0 },
  );
  // Départ au tout premier niveau de la campagne : vagues clémentes, le tir
  // automatique tient le vaisseau en vie sans qu'il ait à viser.
  world.startLevel(levelAt(CAMPAIGN_START) ?? 'monde-1', WORLDS[0]!.hpScale, 0);
}
